import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';

@Component({
  selector: 'usb-transfer-form',
  templateUrl: './transfer-form.component.html',
  styleUrls: ['./transfer-form.component.scss']
})
export class UsbTransferFormComponent {
  @Input() formGroup: FormGroup;
  @Input() selectedFile: any;
  @Input() selectedClient: any;
  @Input() availablePax: any[];
  @Input() loadingFiles: boolean;
  @Input() loadingPax: boolean;
  @Input() loadingCountries: boolean;
  @Input() showNewFileNameInput: boolean;
  @Input() showNewClientInput: boolean;
  @Input() existingFiles: any[];
  @Input() countriesData: any[];
  @Input() newFilePassenger: string;
  @Input() newFilePassengerError: boolean;
  @Input() newFilePhoneCode: string;
  @Input() newFilePhoneCodeError: boolean;
  @Input() newFilePhone: string;
  @Input() newFilePhoneError: boolean;
  @Input() newFileEmail: string;
  @Input() newFileEmailError: boolean;
  @Input() phoneCodeSelected: string;
  @Input() selectedFileId: string | null;
  @Input() selectedClientId: string | null;
  @Input() edit: boolean = false;
  @Input() requiredFieldMessage: (tab: string, field: string) => string;
  @Input() getFileLabel: (f: any) => string;
  @Input() getClientLabel: (c: any) => string;
  @Input() getClientsForSelectedFile: () => any[];
  @Input() isNuevoFileValid: () => boolean;
  @Input() blockInvalidKeys: (event: KeyboardEvent, type: string) => void;
  @Input() requestReady: boolean;
  @Input() showClearDialog: boolean = false;

  @Output() submit = new EventEmitter<any>();
  @Output() clear = new EventEmitter<void>();
  @Output() clearDialogChange = new EventEmitter<boolean>();
  @Output() fileChange = new EventEmitter<any>();
  @Output() clientChange = new EventEmitter<any>();
  @Output() paxChange = new EventEmitter<any>();
  @Output() requestTypeChange = new EventEmitter<any>();
  @Output() newFileToggle = new EventEmitter<void>();
  @Output() newClientToggle = new EventEmitter<void>();
  @Output() newFileSubmit = new EventEmitter<void>();
  @Output() newClientSubmit = new EventEmitter<any>();
  @Output() fileSearchInput = new EventEmitter<string>();
  @Output() clearSelectedFile = new EventEmitter<void>();
  @Output() passengerIdChange = new EventEmitter<any>();
  @Output() passengerFirstNameChange = new EventEmitter<string>();
  @Output() passengerLastNameChange = new EventEmitter<string>();
  @Output() newFilePassengerInput = new EventEmitter<string>();
  @Output() newFilePhoneCodeInput = new EventEmitter<string>();
  @Output() newFileCountrySelected = new EventEmitter<any>();
  @Output() newFilePhoneInput = new EventEmitter<string>();
  @Output() newFileEmailInput = new EventEmitter<string>();
  @Output() countrySelected = new EventEmitter<any>();
  @Output() phoneCodeInput = new EventEmitter<string>();
  @Output() clientFirstNameChange = new EventEmitter<string>();
  @Output() clientLastNameChange = new EventEmitter<string>();
  @Output() clientEmailChange = new EventEmitter<string>();
  @Output() clientPhoneChange = new EventEmitter<string>();

  readonly modalityOptions = ['Privado', 'Regular'];
  readonly categoryOptions = ['Estándar', 'Lujo'];
  readonly ageOptions = Array.from({ length: 18 }, (_, i) => i);

  readonly vehicleOptions = [
    'Sedán', 'Van', 'Minivan', 'Camioneta', 'Minibús',
    'Autobús', 'Vehículo de lujo', 'Limusina', 'Pick-up', 'Blindado'
  ];

  constructor(private fb: FormBuilder) {}

  get vehicleOptionsFirstHalf(): string[] {
    const half = Math.ceil(this.vehicleOptions.length / 2);
    return this.vehicleOptions.slice(0, half);
  }

  get vehicleOptionsSecondHalf(): string[] {
    const half = Math.ceil(this.vehicleOptions.length / 2);
    return this.vehicleOptions.slice(half);
  }

  get childAgesArray(): FormArray {
    return this.formGroup.get('childAges') as FormArray;
  }

  get childrenCount(): number {
    return Math.max(0, Math.floor(Number(this.formGroup.get('children')?.value) || 0));
  }

  get totalPassengers(): number {
    return (Number(this.formGroup.get('adults')?.value) || 0) + this.childrenCount;
  }

  isModalitySelected(value: string): boolean {
    return (this.formGroup.get('modality')?.value || []).includes(value);
  }

  isCategorySelected(value: string): boolean {
    return (this.formGroup.get('category')?.value || []).includes(value);
  }

  setModality(value: string): void {
    this.formGroup.patchValue({ modality: [value] });
  }

  setCategory(value: string): void {
    this.formGroup.patchValue({ category: [value] });
  }

  toggleVehicle(value: string): void {
    const current = this.formGroup.get('vehicles')?.value || [];
    const next = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
    this.formGroup.patchValue({ vehicles: next });
  }

  isVehicleSelected(value: string): boolean {
    return (this.formGroup.get('vehicles')?.value || []).includes(value);
  }

  onChildrenChange(value: string): void {
    const count = Math.max(0, Math.floor(Number(value) || 0));
    const current = this.childAgesArray;
    while (current.length < count) current.push(this.fb.control(''));
    while (current.length > count) current.removeAt(current.length - 1);
  }

  onChildAgeChange(index: number, age: string): void {
    this.childAgesArray.at(index).setValue(age);
  }

  openDatePicker(field: string): void {
  }

  getReturnDateError(): string {
    if (!this.formGroup.get('returnDate')?.touched) return '';
    if (this.formGroup.hasError('returnDateRequired')) return 'Seleccioná la fecha de vuelta.';
    if (this.formGroup.hasError('dateRange')) return 'La vuelta no puede ser anterior a la recogida.';
    return '';
  }

  getReturnTimeError(): string {
    if (!this.formGroup.get('returnTime')?.touched) return '';
    if (this.formGroup.hasError('returnTimeRequired')) return 'Seleccioná la hora de vuelta.';
    if (this.formGroup.hasError('dateTimeRange')) return 'La hora de vuelta debe ser posterior a la de recogida.';
    return '';
  }

  confirmClear(): void {
    this.showClearDialog = true;
    this.clearDialogChange.emit(true);
  }

  cancelClear(): void {
    this.showClearDialog = false;
    this.clearDialogChange.emit(false);
  }

  confirmClearAction(): void {
    this.formGroup.reset({
      requestType: 'quotation',
      file_id: '', fileName: '',
      client: { client_id: '', first_name: '', last_name: '', email: '', phone_code: '', phone: '' },
      passenger: { id: '', first_name: '', last_name: '' },
      origin: '', destination: '',
      pickupDate: '', pickupTime: '',
      returnDate: '', returnTime: '',
      adults: '1', children: '0', childAges: [],
      modality: [], category: [], vehicles: [],
      comments: ''
    });
    this.showClearDialog = false;
    this.clearDialogChange.emit(false);
    this.clear.emit();
  }

  onSubmit(): void {
    if (this.formGroup.invalid || !this.requestReady) return;
    this.submit.emit(this.formGroup.value);
  }
}
